import { HotelCancellationLabel } from './HotelConditionLabel';
import { Box, Button, Collapse, Stack, Typography } from '@mui/material';
import { useCallback, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import type { HotelCancellationView } from '@bandido/shared/types/hotels/hotel-cancellation';

/** Invalidate a snapshot at its exclusive boundary without interpreting the next penalty. */
export function cancellationBoundary(policy?: HotelCancellationView | null): number | null {
  const dates = [policy?.next_change_at, policy?.state === 'free_now' ? policy.free_cancellation_until : null]
    .filter((value): value is string => !!value)
    .map((value) => Date.parse(value))
    .filter(Number.isFinite);
  return dates.length ? Math.min(...dates) : null;
}

export function subscribeCancellationBoundary(boundary: number | null, changed: () => void): () => void {
  if (boundary === null || Date.now() >= boundary) return () => {};
  let timer: ReturnType<typeof setTimeout>;
  const check = () => {
    const remaining = boundary - Date.now();
    if (remaining <= 0) changed();
    else timer = setTimeout(check, Math.min(remaining, 2147483647));
  };
  check();
  return () => clearTimeout(timer);
}

/** Read-only provider interpretation. Display exact units; never calculate a penalty. */
export function HotelCancellation({
  policy,
  compact = false,
  renderDetails = true,
  detailsOpen,
  onDetailsToggle,
}: {
  policy?: HotelCancellationView | null;
  compact?: boolean;
  renderDetails?: boolean;
  detailsOpen?: boolean;
  onDetailsToggle?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const boundary = cancellationBoundary(policy);
  const subscribe = useCallback((changed: () => void) => subscribeCancellationBoundary(boundary, changed), [boundary]);
  const expired = useCallback(() => boundary !== null && Date.now() >= boundary, [boundary]);
  const stale = useSyncExternalStore(subscribe, expired, expired);
  const date = (value: string | null | undefined) =>
    value
      ? new Intl.DateTimeFormat(i18n.language, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'UTC',
          timeZoneName: 'short',
        }).format(new Date(value))
      : t('hotelSearch.noDeadline');
  return (
    <Stack gap={0.5}>
      <HotelCancellationLabel state={stale ? 'unknown' : (policy?.state ?? 'unknown')} />
      {stale && <Typography variant="caption">{t('hotelSearch.policyExpired')}</Typography>}
      {!stale && policy?.state === 'free_now' && policy.free_cancellation_until && (
        <Typography variant="caption">
          {t('hotelSearch.freeUntil', { time: date(policy.free_cancellation_until) })}
        </Typography>
      )}
      {!compact && policy && (
        <>
          <Button
            sx={{ alignSelf: 'flex-start', px: 0, minHeight: 36, fontSize: 12 }}
            size="small"
            onClick={onDetailsToggle ?? (() => setOpen(!open))}
            aria-expanded={detailsOpen ?? open}
          >
            {t('hotelSearch.cancellationDetails')}
          </Button>
          {renderDetails && (
            <Collapse in={detailsOpen ?? open}>
              <HotelCancellationDetails policy={policy} />
            </Collapse>
          )}
        </>
      )}
    </Stack>
  );
}

export function HotelCancellationDetails({ policy }: { policy: HotelCancellationView }) {
  const { t, i18n } = useTranslation();
  const date = (value: string | null | undefined) =>
    value
      ? new Intl.DateTimeFormat(i18n.language, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'UTC',
          timeZoneName: 'short',
        }).format(new Date(value))
      : t('hotelSearch.noDeadline');
  return (
    <Stack gap={1} sx={{ p: 1.25, bgcolor: 'action.hover', borderRadius: 1, minWidth: 0 }}>
      <Typography variant="caption">
        {t(policy.source === 'quote' ? 'hotelSearch.policyFromQuote' : 'hotelSearch.policyFromSearch')}
      </Typography>
      {policy.description && (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
          {policy.description}
        </Typography>
      )}
      <Box
        component="table"
        aria-label={t('hotelSearch.cancellationDetails')}
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          fontSize: 12,
          '& th': { fontWeight: 400, textAlign: 'left', pb: 1, color: 'text.secondary' },
          '& td': {
            py: 1,
            verticalAlign: 'top',
            borderTop: 1,
            borderColor: 'divider',
            overflowWrap: 'anywhere',
          },
        }}
      >
        <thead>
          <tr>
            <th style={{ width: '60%' }}>{t('hotelSearch.policyWhen')}</th>
            <th style={{ textAlign: 'right' }}>{t('hotelSearch.policyPenalty')}</th>
          </tr>
        </thead>
        <tbody>
          {policy.periods.map((period, i) => (
            <tr key={i}>
              <td style={{ paddingRight: 8 }}>
                {period.starts_at && (
                  <>
                    <span>{t('hotelSearch.policyFrom', { time: date(period.starts_at) })}</span>
                    <br />
                  </>
                )}
                <span>{t('hotelSearch.policyBefore', { time: date(period.ends_at) })}</span>
              </td>
              <td style={{ textAlign: 'right' }}>
                <Typography variant="caption">{t(`hotelSearch.cancellationState.${period.state}`)}</Typography>
                {period.penalties.map((p, j) => (
                  <Typography key={j} variant="caption" display="block" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {p.amount
                      ? `${p.amount.currency} ${p.amount.amount}`
                      : p.terms?.value != null
                        ? `${p.terms.value} ${p.terms.penalty_type === 'PERCENT' ? '%' : p.terms.penalty_type === 'NIGHTS' ? t('hotelSearch.penaltyNights') : (p.terms.penalty_type ?? '')}`
                        : t('hotelSearch.penaltyUnknown')}
                    {p.description ? ` · ${p.description}` : ''}
                  </Typography>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </Box>
      <Typography variant="caption">{t('hotelSearch.policyLimitations')}</Typography>
    </Stack>
  );
}
